/**
 * Hook que gerencia eventos de clique no mapa para clusters e marcadores individuais.
 * Funcionalidades:
 * - Seleciona veículo quando clicado em um marcador individual
 * - Para clusters, ajusta o zoom e centraliza o mapa para mostrar todos os marcadores do grupo
 * - Para marcadores únicos, aumenta o zoom e centraliza no marcador
 * - Previne duplo clique usando EventUtils
 */
import EventUtils from '@rio-cloud/rio-uikit/EventUtils';
import { ICoordinates } from '../interfaces';
import { getBoundingBox, getCenter, getZoomForBounds } from '../functions';

export function useClusterEventMap({
  onSelectVehicle,
  setMapCenter,
  setMapZoom,
  mapApiRef,
  mapContainerRef,
}: {
  onSelectVehicle?: (vehicleId: string) => void;
  setMapCenter: (center: { lat: number; lng: number }) => void;
  setMapZoom: (zoom: number) => void;
  mapApiRef: React.RefObject<any>;
  mapContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const updateMapView = (center: { lat: number; lng: number }, zoom: number) => {
    setMapCenter(center);
    setMapZoom(zoom);
    if (mapApiRef.current?.map) {
      mapApiRef.current.map.setCenter(center, true);
      mapApiRef.current.map.setZoom(zoom, true);
    }
  };

  const trySelectVehicle = (target: any) => {
    const vehicleId = target?.data?.em?.data?.id || target?.data?.hm?.data?.id;
    if (onSelectVehicle && vehicleId) {
      onSelectVehicle(vehicleId);
      return true;
    }
    return false;
  };

  const handleCluster = (target: any) => {
    if (typeof target.getData !== "function") return;

    const data = target.getData();
    const coords: ICoordinates[] =
      (Array.isArray(data.markers) && data.markers) ||
      (Array.isArray(data.children) && data.children) ||
      [];

    if (coords.length > 1) {
      const bbox = getBoundingBox(coords);
      const center = getCenter(bbox);
      const width = mapContainerRef.current?.offsetWidth || 800;
      const height = mapContainerRef.current?.offsetHeight || 600;
      const zoom = Math.max(5, Math.min(getZoomForBounds(bbox, width, height), 18));
      updateMapView(center, zoom);
    }
  };

  const handleSingleMarker = (coords: { lat?: number; lng?: number } | undefined) => {
    if (!coords?.lat || !coords?.lng) return;
    const center = { lat: coords.lat, lng: coords.lng };
    const zoom = Math.min(18, mapApiRef.current?.map?.getZoom?.() + 2 || 10);
    updateMapView(center, zoom);
  };

  return EventUtils.createTapOnDblTapPreventer({
    [EventUtils.TAP]: (event: any) => {
      const target = event.target;

      if (trySelectVehicle(target)) return;

      handleCluster(target);
      handleSingleMarker(target.im);
      handleSingleMarker(target.lm);
    },
  });
}
